<template>
	<Popover
		:open="open"
		:top="top"
		:left="left"
		:offsetY="offsetY"
		:scheme="scheme"
		:boundary="boundary"
		ref="popover"
		:tabindex="tabindex"
		@keydown.enter.stop.prevent="accessibilityON = true"
		@keydown.esc.stop.prevent="accessibilityON = false"
	>
		<slot />
		<template #content>
			<Accessible
				name="popover-menu"
				focusFirstItem
				autoFocus
				trapped
				:tabOrder="tabOrder"
				@blurOut="closeMenu"
			>
				<div
					class="menu"
					:class="{ dark: isDark, [menuClass]: menuClass }"
					ref="menuContainer"
				>
					<div class="menu-section" v-for="(sect, i) in menuSections" :key="i">
						<TextDivider v-if="sect.divider" :text="sect.divider" />
						<div
							class="menu-item"
							v-for="(item, i) in sect.items"
							:key="item.id || i"
							:class="{
								primary: item.primary,
								danger: item.danger,
								icon: !!item.icon,
								[item.class || '']: true,
							}"
							@click="select(item, $event)"
						>
							<slot
								v-if="item.type !== DIVIDER"
								name="item"
								:item="item"
								:index="i"
							>
								<slot name="icon">
									<component
										v-if="item.icon"
										:is="item.icon"
										v-bind="item.iconProps"
										class="menu-item-icon"
									/>
								</slot>
								{{ getLabel(item) }}
							</slot>
						</div>
					</div>
				</div>
			</Accessible>
		</template>
	</Popover>
</template>

<script>
import Accessible from '@/components/common/Accessible'
import TextDivider from '@/components/common/TextDivider'
import Popover, { closeAllPopovers } from './Popover'
import { colorSchemes } from '@/helpers/constants'

export const closeAllMenus = () => {
	closeAllPopovers()
}

export const DIVIDER = 'DIVIDER'

export default {
	name: 'Menu',
	components: { Popover, Accessible, TextDivider },

	props: {
		accessibilityKeyEvents: Object,
		items: [Array, Object],
		open: { type: Boolean, default: null },
		top: { type: Boolean, default: null },
		left: { type: Boolean, default: null },
		offsetY: Number,
		boundary: Number,
		localize: { type: Boolean, default: null },
		tabindex: String,
		menuClass: String,
		scheme: { type: String, default: colorSchemes.LIGHT },
	},

	data() {
		return {
			DIVIDER,
			accessibilityON: false,
		}
	},

	emits: ['select', 'open'],

	computed: {
		isOpen() {
			return this.$refs.popover.isOpen
		},
		isDark() {
			return this.scheme === colorSchemes.DARK
		},
		menuSections() {
			const [first] = this.items
			if (first && Array.isArray(first.items)) {
				return this.items
			}
			return [{ items: this.items }]
		},
		tabOrder() {
			return [
				{
					all: '.menu-item',
					clickOnEnter: true,
					arrows: true,
					...(this.accessibilityKeyEvents || {}),
				},
			]
		},
	},

	methods: {
		select(item, e) {
			this.closeMenu()
			this.$emit('select', item, e)
		},

		getLabel(item) {
			const label = item.label || item
			return this.localize ? this.$t(label) : label
		},

		closeMenu() {
			this.$refs.popover.closePopover()
		},

		toggleMenu() {
			this.$refs.popover.togglePopover()
		},
	},

	mounted() {
		this.$refs.popover.$watch('isOpen', (newVal) => {
			this.isMenuOpen = newVal
			this.$emit('open', newVal)
		})
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.menu {
	background: var(--neutral-0);
	box-shadow: 0 1px 4px 0 #c4c4c4;
	border-radius: 6px;
	overflow: hidden;
	&.dark {
		// background: var(--neutral-100);
		box-shadow: 0 1px 4px 0 black;
		.menu-item {
			color: white;
			&:hover {
				color: var(--teal-300);
			}
		}
	}
	.menu-item {
		@include flex(16px);
		min-width: 120px;
		cursor: pointer;
		width: 100%;
		padding: 8px 16px;
		white-space: nowrap;
		color: #3a3a3a;
		font-size: 14px;
		font-family: Avenir-Book;
		&:hover {
			color: var(--blue-300);
		}
		&.danger {
			color: var(--red-200);
			&:hover {
				color: var(--red-300);
			}
		}
		.menu-item-icon {
			flex-shrink: 0;
			font-size: 14px;
		}
	}
}
</style>

<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.menu {
		box-shadow: 0 1px 4px 0 black;
		.menu-item {
			color: white;
			&:hover {
				color: var(--teal-300);
			}
		}
		.menu-item {
			color: var(--neutral-600);
		}
	}
}
</style>
